import styled from 'styled-components';

/* Primitivas de formulario compartidas — una sola fuente de verdad para
   inputs, labels y mensajes en todas las páginas (user, empresa y admin). */

export const FormGroup = styled.div`
  margin-bottom: 18px;
`;

export const FormLabel = styled.label`
  display: block;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
`;

const fieldBase = `
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 12px 14px;
  background: var(--bg-input);
  border: 1.5px solid transparent;
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-family: var(--font-brand);
  font-size: var(--text-base);
  transition: border-color var(--transition-fast), background var(--transition-fast), box-shadow var(--transition-fast);

  &::placeholder { color: var(--text-muted); }

  &:hover:not(:disabled) { border-color: var(--border-medium); }

  &:focus {
    outline: none;
    background: var(--white);
    border-color: var(--border-focus);
    box-shadow: 0 0 0 4px var(--focus-ring);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const FormInput = styled.input`${fieldBase}`;
export const FormHint = styled.small`
  display: block;
  margin-top: 6px;
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: 1.4;
`;

export const FormSelect = styled.select`
  ${fieldBase}
  cursor: pointer;
`;

export const FormTextarea = styled.textarea`
  ${fieldBase}
  resize: vertical;
  min-height: 90px;
  line-height: 1.55;
`;

export const ErrorMessage = styled.div`
  background: var(--danger-subtle);
  border: 1px solid var(--danger-light);
  color: var(--danger-dark);
  padding: 12px 16px;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  margin-bottom: 18px;
`;

export const SuccessMessage = styled.div`
  background: var(--success-subtle);
  border: 1px solid var(--success-light);
  color: var(--success-dark);
  padding: 12px 16px;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  margin-bottom: 18px;
`;

/* Rejillas de formulario que colapsan a una columna en teléfono */
export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols || 2}, minmax(0, 1fr));
  gap: var(--spacing-md);

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

/* Fila de interruptor + texto */
export const SwitchGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 14px 0;

  strong {
    display: block;
    font-size: var(--text-sm);
  }

  p {
    font-size: var(--text-xs);
    color: var(--text-secondary);
  }

  > span {
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }
`;

export const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + .slider {
      background: var(--primary);
    }

    &:checked + .slider::before {
      transform: translateX(20px);
    }

    &:focus-visible + .slider {
      box-shadow: 0 0 0 3px var(--focus-ring-strong);
    }
  }

  .slider {
    position: absolute;
    inset: 0;
    background: var(--border-medium);
    border-radius: var(--radius-round);
    cursor: pointer;
    transition: background var(--transition-fast);

    &::before {
      content: '';
      position: absolute;
      width: 18px;
      height: 18px;
      left: 3px;
      top: 3px;
      background: var(--white);
      border-radius: 50%;
      transition: transform var(--transition-fast);
      box-shadow: var(--shadow-xs);
    }
  }
`;

/* Botones al pie de un formulario/modal; a una columna en teléfono */
export const FormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm) var(--spacing-md);
  justify-content: flex-end;
  margin-top: var(--spacing-lg);

  @media (max-width: 560px) {
    > * { flex: 1 1 100%; }
  }
`;

/* Tarjeta centrada para páginas de autenticación (login/registro) */
export const AuthContainer = styled.div`
  min-height: calc(100vh - 68px - 40px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 20px;
  background: var(--gradient-page-glow), var(--bg-section);
`;

export const AuthCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  padding: 40px 36px;
  width: 100%;
  max-width: ${props => props.$wide ? '640px' : '440px'};

  @media (max-width: 560px) {
    padding: 28px 20px;
    border-radius: var(--radius-lg);
  }
`;

export const AuthTitle = styled.h1`
  font-family: var(--font-brand);
  font-size: var(--text-3xl);
  font-weight: 600;
  text-align: center;
  margin-bottom: 6px;
  letter-spacing: -0.5px;
`;

export const AuthSubtitle = styled.p`
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  margin-bottom: 28px;
`;

export const AuthFooter = styled.div`
  margin-top: 24px;
  text-align: center;
  font-size: var(--text-sm);
  color: var(--text-secondary);

  p { margin: 8px 0; }

  hr {
    border: none;
    border-top: 1px solid var(--border-light);
    margin: 16px 0;
  }

  a {
    color: var(--primary);
    font-weight: 600;
  }
`;

/* Cabecera de página interna: eyebrow + título + descripción */
export const PageHeader = styled.header`
  margin-bottom: 32px;

  h1 {
    font-family: var(--font-brand);
    font-size: var(--text-3xl);
    font-weight: 600;
    letter-spacing: -0.5px;
    margin-bottom: 4px;
  }

  p {
    color: var(--text-secondary);
    font-size: var(--text-base);
  }
`;

export const PageEyebrow = styled.span`
  display: inline-block;
  background: var(--primary-subtle);
  color: var(--primary);
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: var(--radius-round);
  margin-bottom: 12px;
`;
